// Privacy Policy — required public-facing legal page. Originally
// authored by the broker; rendered here verbatim with structural
// formatting (headings, lists) so users can scan it on screen and
// Google's classifier can detect it as a real privacy disclosure
// (a major trust signal for the "Deceptive pages" Safe Browsing
// classifier when sites collect personal information through forms).
import React from "react";
import PublicFooter from "./PublicFooter";
import SeoHead from "../../site/SiteSeo";

const CONTACT = {
  email: "sayhello@hellomortgage.ca",
  phone: "780-900-2495",
  brokerage_address: "BRX Mortgage — 500 4th Avenue SW, Suite 2500, Calgary, AB T2P 2V6",
};

export default function PrivacyPolicy() {

  return (
    <div className="min-h-screen bg-white">
      <SeoHead title="Privacy Policy | How Hello Mortgage Protects Your Information" description="How Hello Mortgage collects, uses and protects your personal information under PIPEDA — what we keep, why, who sees it, and how to reach us about it." path="/privacy/" />
      <article
        className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14"
        data-testid="privacy-policy-page"
      >
        <header className="mb-8">
          <a
            href="/"
            className="text-xs font-bold uppercase tracking-[0.18em] text-brand-red hover:text-brand-navy transition-colors font-body"
            data-testid="privacy-policy-back"
          >
            ← Hello Mortgage
          </a>
          <h1 className="text-4xl sm:text-5xl font-display text-brand-navy mt-3 mb-2">
            Privacy Policy
          </h1>
          <p className="text-base text-slate-600 font-body">
            Say Hello To The Easiest Way To Mortgage
          </p>
        </header>

        <div className="prose-content space-y-5 text-slate-700 font-body leading-relaxed text-[15px]">
          <p>
            We respect the privacy of our clients and we are committed to keeping
            your personal information accurate, confidential, secure and private.
            We have adopted the following Privacy Policy to ensure that we continue
            to meet our commitment to your privacy.
          </p>
          <p className="italic text-slate-500 text-sm">
            The following sections, read as a whole, comprise our Privacy Policy.
          </p>

          <h2 className="text-2xl font-display text-brand-navy pt-4">Introduction</h2>
          <p>
            In the course of providing our services, we may collect certain
            personal information about its clients with their consent.
            "Personal Information" means information about an identifiable
            individual. This may include, without limitation, the individual's
            name, home address, age, income, credit history or other financial
            information, credit card information, personal preferences and other
            information about his or her family. Personal Information does not
            include the name, title, business address or telephone number of an
            employee of an organization.
          </p>
          <p>
            In the course of providing services to customers we routinely share
            this information with others such as credit reporting agencies,
            financial institutions, and other suppliers who are directly involved
            in the process.
          </p>
          <p>
            Our Privacy Policy is based on, and complies with, Canada's{" "}
            <strong>Personal Information Protection and Electronic Documents Act
            ("PIPEDA")</strong>, which includes the Ten Privacy Principles outlined
            in the Canadian Standards Association Model Code for the Protection of
            Personal Privacy. For more information about this legislation, please
            visit the official website of the{" "}
            <a
              href="https://www.priv.gc.ca/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-red font-medium hover:underline"
            >
              Privacy Commissioner of Canada
            </a>
            .
          </p>
          <p>
            This Privacy Policy describes the ten principles that we follow to
            ensure that we protect your Personal Information when we collect, use
            or disclose it in the course of carrying on commercial activities in
            Canada. All of our employees who have access to Personal Information
            must adhere to our Privacy Policy and related procedures.
          </p>

          <h2 className="text-2xl font-display text-brand-navy pt-4">
            The Ten Principles of Privacy
          </h2>
          <p>
            The following ten principles govern our actions as they relate to the
            use of Personal Information:
          </p>

          <Principle
            number={1}
            title="Accountability"
            body="We are accountable for all Personal Information in our possession or custody, including Personal Information disclosed to third parties for purposes of providing services requested by you."
          />
          <Principle
            number={2}
            title="Identifying Purposes"
            body="We will inform you of the purposes for which we are collecting any Personal Information, before or at the time the information is collected. Often the purpose is outlined within the body of any application or contract for services you may execute when dealing with us."
          />
          <Principle number={3} title="Consent">
            <p>
              We will obtain your consent before or when we collect, use or
              disclose your Personal Information.
            </p>
            <p>
              Consent can be express, implied, or given through an authorized
              representative. You may withdraw your consent at any time (subject
              to any legal consequences of which you will be informed). We may
              collect, use or disclose Personal Information without your knowledge
              or consent in exceptional circumstances where such collection, use
              or disclosure is permitted or required by law.
            </p>
          </Principle>
          <Principle
            number={4}
            title="Limiting Collection"
            body="The information collected by us will be limited to those details necessary for the purposes we have identified to you or to perform the services you have requested. Information will be collected by fair and lawful means."
          />
          <Principle number={5} title="Limiting Use, Disclosure and Retention">
            <p>
              Personal Information will only be used or disclosed for the purpose
              for which it was collected unless you have otherwise consented.
              Personal Information may only be retained for the amount of time
              needed to fulfill the purpose for which it was collected.
            </p>
            <p>
              In certain exceptional circumstances, we may have a legal duty or
              right to disclose Personal Information without your knowledge or
              consent with respect to matters that concern the company's or the
              public's interest.
            </p>
          </Principle>
          <Principle
            number={6}
            title="Accuracy"
            body="We will keep Personal Information as accurate, complete and current as necessary to fulfill the identified purposes for which it was collected. You may have this information amended where it is found to be inaccurate or incomplete."
          />
          <Principle
            number={7}
            title="Safeguarding Personal Information"
            body="Personal Information is safeguarded using measures appropriate to the sensitivity of the information and in keeping with generally accepted industry standards."
          />
          <Principle
            number={8}
            title="Openness"
            body="We will make information available to our clients and employees about the policies and procedures we use to manage personal information. You have access to this information through our website, or through alternate means if requested."
          />
          <Principle
            number={9}
            title="Access"
            body="Upon written request, you will be informed of the existence, use and disclosure of your Personal Information, and will be given access to it. We will respond to such requests as efficiently as possible. If we are prohibited from providing such access, we will explain the reasons for the lack of access, except where prohibited by law."
          />
          <Principle
            number={10}
            title="Addressing Complaints and Suggestions"
            body="You may challenge compliance with this Privacy Policy. We have policies and procedures to receive, investigate and respond to complaints and questions regarding this Privacy Policy and our collection, use and disclosure of Personal Information. You may contact our Privacy Officer to express any concerns or to request access to your Personal Information. Contact information is provided at the end of this document."
          />

          <h2 className="text-2xl font-display text-brand-navy pt-4">
            How we treat your personal information
          </h2>

          <h3 className="text-lg font-display text-brand-navy pt-2">
            The Kind of Information We Collect
          </h3>
          <p>
            We gather and use Personal Information in order to provide you with
            the products and services you have requested, namely: information that
            is required to effectively provide products and services. We may also
            use your Personal Information to update you about our services and to
            offer you additional products or services that you may be interested
            in, so that we may continue to serve your needs.
          </p>
          <p>
            If you visit our website(s), we do not collect personally identifiable
            information about you unless you provide it. All information that you
            do provide us with is kept strictly confidential. This Privacy Policy
            does not apply to aggregated data from which it is not possible to
            determine the identity of a specific individual. We reserve the right
            to use aggregated data in any way that we determine appropriate.
          </p>

          <h3 className="text-lg font-display text-brand-navy pt-2">
            How We Use Your Information
          </h3>
          <p>We use Personal Information for three general purposes:</p>
          <ol className="list-decimal pl-6 space-y-2">
            <li>
              To communicate with you in order to provide you with our products
              and services.
            </li>
            <li>
              For inclusion in documentation relating to your particular financial
              transaction.
            </li>
            <li>
              Transactional information to monitor our level of service and report
              back to you to ensure your satisfaction with the provision of such
              services.
            </li>
          </ol>
          <p>
            We will only use Personal Information for the purpose that we have
            disclosed to you. If we want to use your information for a different
            purpose, we will notify you and ask for your consent first.
          </p>

          <h3 className="text-lg font-display text-brand-navy pt-2">
            When We Are Permitted to Disclose Your Information
          </h3>
          <p>
            We are obliged to keep your Personal Information confidential except
            under the following circumstances:
          </p>
          <ol className="list-decimal pl-6 space-y-1">
            <li>When authorized by you</li>
            <li>When required by law</li>
            <li>When permitted by law</li>
          </ol>

          <h4 className="font-bold text-slate-800 pt-2">When Authorized by You</h4>
          <p>
            Many of the services we offer require us to obtain Personal Information
            about you in order to perform the services you have engaged us to
            provide. We will always obtain consent from you first, and we will
            never use the information for purposes other than those we have told
            you about.
          </p>
          <p>
            You may withdraw your consent at any time, subject to any legal
            implications (which we will inform you about). In some cases, if you
            do not consent to our use or disclosure of certain Personal
            Information, we may be unable to continue to provide all or part of
            the services you have requested.
          </p>

          <h4 className="font-bold text-slate-800 pt-2">When Required by Law</h4>
          <p>
            The type of information we are legally required to disclose usually
            relates to government tax reporting requirements. However in some
            cases, such as under a court order, we may be required to disclose
            certain information to persons specified in the court order. We will
            only provide the specific information requested and only upon being
            satisfied that the authorities have legitimate grounds to request the
            information.
          </p>

          <h4 className="font-bold text-slate-800 pt-2">When Permitted by Law</h4>
          <p>
            The legislation has provided certain situations where we are legally
            permitted to disclose Personal Information without your consent.
            Examples include situations involving the collection of debt in
            arrears, medical emergencies, or suspicion of illegal activities.
          </p>

          <h3 className="text-lg font-display text-brand-navy pt-2">
            Website Security
          </h3>
          <p>
            In order to serve you better, we offer client access to certain
            information through this website. This website contains links to other
            websites, including those of its licensees and business partners. We
            are in no way responsible and cannot guarantee the content or privacy
            of other sites linked from this website.
          </p>

          <h3 className="text-lg font-display text-brand-navy pt-2">
            Accessing and Amending Your Information
          </h3>
          <p>
            As a client you have the right to access the Personal Information we
            keep in your file and you have the right to verify or amend the
            information if it is shown to be inaccurate. If you would like to view
            the Personal Information held in your file, please make a written
            request to the Privacy Officer at the address listed below. We will
            respond to your request as efficiently as possible. To make a change,
            please make a similar request in writing to the Privacy Officer.
          </p>

          <h3 className="text-lg font-display text-brand-navy pt-2">Opting Out</h3>
          <p>
            In order to provide you with our services and products, we will, with
            your consent, contact you to provide you with information on our
            products and services and may share your personal information with
            other affiliates. Should you not want to receive promotional materials
            from or have your Personal Information shared with these companies
            please contact the Privacy Officer at the address, phone number or
            email address provided at the end of this document.
          </p>

          <h3 className="text-lg font-display text-brand-navy pt-2">
            Anonymous Information and "Cookies"
          </h3>
          <p>
            We may collect anonymous information about you. This means that the
            information collected cannot be traced back to a specific person. For
            example, our web servers may record certain information automatically
            when you visit our websites. This information is collected using
            "cookies" and might include the pages you visited, your IP (Internet
            Protocol) address and other site usage statistics. This anonymous
            information is used for research and analytical purposes only (like
            evaluating how many visitors our websites receive or which pages they
            visit most often). It does not reveal any Personal Information about
            you, the user. This aggregate data may be disclosed to third parties,
            but never with personally identifying information.
          </p>

          <h4 className="font-bold text-slate-800 pt-2">"Cookies"</h4>
          <p>
            "Cookies" are small text files that contain a unique identification
            number that allows our computers to identify your web browser (but not
            you) each time you visit one of our websites that uses cookies. The
            information helps us improve the functionality of the site and
            enhance the navigation and security of your session. Most major
            websites use this technology and most browsers are set up to accept
            them.
          </p>
          <p>
            You may configure your browser to notify you when you receive a
            cookie, and you may refuse to accept them entirely. However, if you
            refuse to accept cookies, you may limit the functionality that we can
            provide to you when you visit our site.
          </p>

          <h2 className="text-2xl font-display text-brand-navy pt-6">
            Questions, Concerns and Complaints
          </h2>
          <p>
            If you have any questions, concerns or complaints about your Personal
            Information, or about our Privacy Policy, please contact us
            immediately:
          </p>
          <div
            className="bg-slate-50 border border-slate-200 rounded-xl p-5 mt-2 not-prose"
            data-testid="privacy-policy-contact"
          >
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-red mb-2 font-body">
              Privacy Officer · Hello Mortgage
            </div>
            <div className="font-body text-slate-800 text-sm space-y-1">
              <div>Matt Broom-Hall · Mortgage Broker &amp; Owner</div>
              <div>
                Email:{" "}
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="text-brand-red font-medium hover:underline"
                >
                  {CONTACT.email}
                </a>
              </div>
              <div>
                Phone:{" "}
                <a
                  href={`tel:+1${CONTACT.phone.replace(/\D/g, "")}`}
                  className="text-brand-red font-medium hover:underline"
                >
                  {CONTACT.phone}
                </a>
              </div>
              <div className="text-slate-600 text-xs pt-1">
                {CONTACT.brokerage_address}
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 italic pt-6">
            Last updated: {new Date().toLocaleDateString("en-CA", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        <PublicFooter />
      </article>
    </div>
  );
}

function Principle({ number, title, body, children }) {
  return (
    <div
      className="border-l-2 border-slate-200 pl-4 py-1"
      data-testid={`privacy-principle-${number}`}
    >
      <div className="text-xs font-bold uppercase tracking-[0.16em] text-brand-red mb-1 font-body">
        Principle {number}
      </div>
      <h3 className="text-base font-bold text-brand-navy mb-2 font-body">
        {title}
      </h3>
      {body && <p className="text-[15px] leading-relaxed">{body}</p>}
      {children}
    </div>
  );
}
